import { formatLabel } from '../utils/formatText';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Gift, Heart, Star, ChevronRight, Truck, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Banner, BannerGrid } from '../components/Banner';
import { brands as mockBrands } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  const [brands, setBrands] = useState(mockBrands);
  const [categories, setCategories] = useState([]);
  const [promoProducts, setPromoProducts] = useState([]);
  const [siteSettings, setSiteSettings] = useState({
    site_email: 'contato@disneylandia.com',
    site_phone: '(94) 99166-6990',
    site_whatsapp: '5594991666990',
    site_address: 'Av. Weyne Cavalcante, 720 - Canaã dos Carajás, PA',
    site_instagram: '@disneylandiacosmeticoscanaa',
    hero_indicator1_title: 'Beleza',
    hero_indicator1_subtitle: 'Cosméticos Premium',
    hero_indicator1_logo: null,
    hero_indicator2_title: '100+',
    hero_indicator2_subtitle: 'Produtos',
    hero_indicator3_title: '100%',
    hero_indicator3_subtitle: 'Originais',
    hero_indicator4_title: 'Presentes',
    hero_indicator4_subtitle: 'Para Toda Ocasião'
  });

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/brands`);
        if (response.data && response.data.length > 0) {
          setBrands(response.data);
        }
      } catch (error) {}
    };
    
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings`);
        if (response.data) {
          setSiteSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {}
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/categories`);
        if (response.data && response.data.length > 0) {
          setCategories(response.data);
        }
      } catch (error) {}
    };

    const fetchPromoProducts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/products`);
        if (response.data && response.data.length > 0) {
          const promos = response.data.filter(p => p.is_promo || p.featured);
          setPromoProducts(promos.length > 0 ? promos.slice(0, 6) : response.data.slice(0, 6));
        }
      } catch (error) {}
    };
    
    fetchBrands();
    fetchSettings();
    fetchCategories();
    fetchPromoProducts();

    const trackView = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/track/view?page=home`);
      } catch (e) {}
    };
    trackView();
  }, []);

  const featuredBrands = brands.filter(b => b.featured);
  const otherBrands = brands.filter(b => !b.featured);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[85vh] flex items-center bg-[#0A0A0A]" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center z-0 opacity-30"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e7485edb-125e-4bcd-87b2-265b3dfb4a6c/images/1240bc7c43ca04a36373bbd30b0c4a84acc50a3718a942c6a49fd661d7569449.png')`,
          }}
        />

        <div className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#EA1266]/20 border border-[#EA1266]/30 rounded-full px-5 py-2.5 mb-8">
              <Sparkles className="text-[#EA1266]" size={16} />
              <span className="text-white text-xs uppercase tracking-[0.2em] font-bold">Cosméticos & Presentes</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-[1.1] tracking-tight">
              Descubra Sua
              <span className="block text-[#EA1266]">
                Beleza Natural
              </span>
            </h1>

            <p className="text-base sm:text-lg text-gray-300 mb-10 leading-relaxed max-w-lg">
              As melhores marcas de cosméticos, perfumaria e presentes especiais para toda ocasião.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Link to="/catalogo">
                <Button className="w-full sm:w-auto bg-[#EA1266] hover:bg-[#d00f58] text-white rounded-full px-8 py-6 text-sm uppercase tracking-[0.15em] font-bold shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 transition-all duration-200" data-testid="hero-catalog-btn">
                  Ver Catálogo
                  <ArrowRight className="ml-3" size={18} />
                </Button>
              </Link>
              <Link to="/orcamento">
                <Button className="w-full sm:w-auto bg-white/10 border border-white/30 text-white hover:bg-white hover:text-[#0A0A0A] rounded-full px-8 py-6 text-sm uppercase tracking-[0.15em] font-bold transition-all duration-200" data-testid="hero-quote-btn">
                  Pedir Orçamento
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
              {[
                { title: siteSettings.hero_indicator1_title, sub: siteSettings.hero_indicator1_subtitle },
                { title: siteSettings.hero_indicator2_title, sub: siteSettings.hero_indicator2_subtitle },
                { title: siteSettings.hero_indicator3_title, sub: siteSettings.hero_indicator3_subtitle },
                { title: siteSettings.hero_indicator4_title, sub: siteSettings.hero_indicator4_subtitle },
              ].map((item, i) => (
                <div key={i} className="bg-white/10 border border-white/10 rounded-2xl p-3 text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{item.title}</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider font-semibold">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Promoções em Destaque */}
      {promoProducts.length > 0 && (
        <section className="py-20 md:py-32 bg-white" data-testid="promo-section">
          <div className="container mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-4 block">Ofertas Especiais</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0A0A0A] tracking-tight">
                Promoções em Destaque
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {promoProducts.map((product) => (
                <Link
                  key={product.id}
                  to={product.slug ? `/produto/${product.slug}` : `/orcamento?produto=${product.id}`}
                  className="group"
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_8px_30px_rgb(234,18,102,0.1)] transition-all duration-300 hover:-translate-y-1">
                    <div className="aspect-square bg-[#F3F4F6] overflow-hidden relative">
                      <img
                        src={product.images?.[0] || 'https://placehold.co/400x400/F3F4F6/999?text=Produto'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {(product.is_promo || product.featured) && (
                        <div className="absolute top-4 left-4 bg-[#EA1266] text-white text-xs uppercase tracking-[0.15em] font-bold px-3 py-1.5 rounded-full">
                          Promo
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-[#EA1266] uppercase tracking-[0.15em] font-bold mb-1">{product.brand}</p>
                      <h3 className="text-base font-medium text-[#0A0A0A] line-clamp-2 group-hover:text-[#EA1266] transition-colors">
                        {product.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/catalogo">
                <Button className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full px-8 py-6 text-sm uppercase tracking-[0.2em] font-medium hover:scale-105 transition-all duration-300" data-testid="promo-view-all-btn">
                  Ver Todos os Produtos
                  <ArrowRight className="ml-3" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Brands Section */}
      <section className="py-20 md:py-32 bg-[#FAFAFA]" data-testid="brands-section">
        <div className="container mx-auto px-6 md:px-12">
          <Banner position="home_hero" className="mb-16" />
          
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-4 block">Nossas Marcas</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0A0A0A] tracking-tight">
              Marcas que Você <span className="text-[#EA1266]">Ama</span>
            </h2>
          </div>

          {/* Featured Brands */}
          <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
            {(featuredBrands.length > 0 ? featuredBrands : brands.slice(0, 2)).map((brand) => (
              <Link
                key={brand.id}
                to={`/catalogo?marca=${brand.name}`}
                className="group relative bg-white p-8 md:p-10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_8px_30px_rgb(234,18,102,0.1)] transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className="bg-[#F3F4F6] p-8 rounded-2xl mb-6 flex items-center justify-center h-[100px]">
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="max-h-12 max-w-[160px] w-auto object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#0A0A0A] group-hover:text-[#EA1266] transition-colors">
                    Ver Produtos {formatLabel(brand.name)}
                  </span>
                  <ChevronRight className="text-gray-400 group-hover:text-[#EA1266] group-hover:translate-x-1 transition-all" size={20} />
                </div>
              </Link>
            ))}
          </div>

          {/* Other Brands */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {(featuredBrands.length > 0 ? otherBrands : brands.slice(2)).map((brand) => (
              <Link
                key={brand.id}
                to={`/catalogo?marca=${brand.name}`}
                className="group bg-white p-4 md:p-6 rounded-xl shadow-sm hover:shadow-[0_8px_30px_rgb(234,18,102,0.08)] transition-all duration-300 hover:-translate-y-0.5 border border-gray-100 flex items-center justify-center h-[70px] md:h-[80px]"
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="max-h-7 md:max-h-9 max-w-[70px] md:max-w-[90px] w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                />
              </Link>
            ))}
          </div>

          <BannerGrid position="home_middle" columns={2} className="mt-16 max-w-4xl mx-auto" />
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-32 bg-white relative" data-testid="categories-section">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-4 block">Explore</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0A0A0A] tracking-tight">
              Nossas <span className="text-[#EA1266]">Categorias</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {categories.length > 0 ? categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/catalogo?cat=${category.id}`}
                className="group"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <Card className="overflow-hidden bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_8px_30px_rgb(234,18,102,0.1)] transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8 relative">
                    <div className="bg-gradient-to-br from-pink-50 to-green-50 w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                      <Sparkles className="text-[#EA1266]" size={24} />
                    </div>
                    
                    <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 group-hover:text-[#EA1266] transition-colors">
                      {category.name}
                    </h3>
                    
                    <div className="flex items-center text-[#EA1266] text-sm font-medium group-hover:gap-2 transition-all">
                      <span>Explorar</span>
                      <ChevronRight className="group-hover:translate-x-1 transition-transform" size={16} />
                    </div>

                    <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#EA1266] to-[#64BC46] w-0 group-hover:w-full transition-all duration-500"></div>
                  </CardContent>
                </Card>
              </Link>
            )) : (
              <div className="col-span-3 text-center py-16">
                <p className="text-gray-400 font-medium">Carregando categorias...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-[#FAFAFA]" data-testid="why-choose-section">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#64BC46] mb-4 block">Nossos Diferenciais</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0A0A0A] tracking-tight">
              Por Que Escolher a <span className="text-[#EA1266]">Disneylândia?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, title: 'Produtos Originais', desc: 'Qualidade garantida em todos os produtos de beleza', color: '#EA1266' },
              { icon: Star, title: 'Marcas Premium', desc: 'As melhores marcas nacionais e importadas', color: '#64BC46' },
              { icon: Truck, title: 'Entrega e Retirada', desc: 'Retire na loja ou receba no conforto da sua casa', color: '#EA1266' },
              { icon: Heart, title: 'Atendimento Especial', desc: 'Equipe pronta para ajudar você a escolher', color: '#64BC46' },
            ].map((item, i) => (
              <Card key={i} className="text-center bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_8px_30px_rgb(234,18,102,0.1)] transition-all duration-300 group hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: `${item.color}15`}}>
                    <item.icon size={28} style={{color: item.color}} />
                  </div>
                  <h3 className="text-lg font-medium mb-3 text-[#0A0A0A]">{item.title}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed font-medium">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EA1266] to-[#d00f58]"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e7485edb-125e-4bcd-87b2-265b3dfb4a6c/images/db3d3c973badd6d3ac39ff6601210952a36ba8bcd2e264eaf87378343e68328d.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="container mx-auto px-6 md:px-12 text-center relative z-10">
          <div className="max-w-3xl mx-auto">
            <Gift className="mx-auto mb-6 text-white/80" size={40} />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-6 tracking-tight">
              Encontre o Presente Perfeito
            </h2>
            <p className="text-base md:text-lg mb-10 text-white/80 leading-relaxed font-medium max-w-xl mx-auto">
              Fale conosco e receba um atendimento personalizado para encontrar exatamente o que você procura.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button className="bg-white text-[#EA1266] hover:bg-gray-100 rounded-full px-8 py-6 text-sm uppercase tracking-[0.2em] font-medium shadow-xl hover:scale-105 transition-all duration-300" data-testid="cta-contact-btn">
                  Fale Conosco
                  <ArrowRight className="ml-3" size={18} />
                </Button>
              </Link>
              <Link to="/catalogo">
                <Button className="bg-white/20 backdrop-blur-md border border-white/40 text-white hover:bg-white hover:text-[#EA1266] rounded-full px-8 py-6 text-sm uppercase tracking-[0.2em] font-medium hover:scale-105 transition-all duration-300" data-testid="cta-catalog-btn">
                  Ver Catálogo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
